/* eslint-disable monorepo/no-internal-import */
import React, {ComponentType} from 'react';
import {registerComponent} from './HydrationProvider';
import {useAddToResourceHints} from './ResourceHintsProvider';

export function lazy<T extends ComponentType<any>>(
  importer: () => Promise<{default: T}>,
  config: {
    /**
     * If true, the lazy-loaded module will be optimized for preloading as a priority resource.
     * Usually, this is only necessary for main Page/Route components.
     * Other modules should be downloaded lazily by react on demand when hydrating the page.
     */
    aboveTheFold?: boolean;
    /**
     * If true, the lazy-loaded module will be treated as an entry point that needs hydration.
     * The useHydrationDone hook will return false until the module has been hydrated on the client.
     * Set this flag to true for components that are Server-Side Rendered, for example, a SSRed Page component.
     *
     * PS: Do not set this to true for components that are only rendered on the client side (e.g. modals, dropdowns, components which are rendered on when inView etc)
     */
    isSsrAndNeedsHydration?: boolean;
  } & (
    | {
        /**
         * The fallback component to show while the lazy-loaded module is loading.
         * If not provided, the Suspense Boundary will render nothing until the module is loaded.
         */
        fallback?: NonNullable<React.ReactNode> | null;
      }
    | {
        /**
         * If true, the Suspense Boundary will be skipped and the module will still be loaded lazily but would not suspend the rendering.
         */
        skipSuspense?: true;
      }
  ) = {},
) {
  const {isSsrAndNeedsHydration, aboveTheFold} = config;

  // Wrap the importer in a retry mechanism
  const reliableImporter = async () => {
    try {
      const module = await importer();
      return module;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('>>>>> Dynamic import failed, retrying:', error);

      // eslint-disable-next-line no-promise-executor-return
      await new Promise(resolve => setTimeout(() => resolve(true), 1000));

      // Try again with after a short delay
      return new Promise<{default: T}>(resolve => {
        importer()
          .then(resolve)
          .catch(secondError => {
            // If you have a fallback component for critical errors
            // you could return that instead of throwing
            throw secondError;
          });
      });
    }
  };

  const LazyLoaded = React.lazy(reliableImporter);

  const componentPath =
    importer.toString().match(/import\(['"](.+?)['"]\)/)?.[1] || '';
  const componentName = componentPath.split('/').pop() || '';
  const componentId = `${componentName}`;

  const Suspensified = React.forwardRef(function Inner(props, ref) {
    if (!process.browser && aboveTheFold) {
      // eslint-disable-next-line react-hooks/rules-of-hooks -- its okay break the rules here because the config.aboveTheFold is a constant, would never change for a given component
      useAddToResourceHints(importer.toString());
    }

    if (config && 'skipSuspense' in config && config.skipSuspense) {
      return <LazyLoaded {...(props as any)} ref={ref} />;
    }

    if (!process.browser) {
      return (
        <React.Suspense fallback={null}>
          <LazyLoaded {...(props as any)} ref={ref} />
        </React.Suspense>
      );
    }

    if (process.browser && isSsrAndNeedsHydration) {
      registerComponent(componentId);
    }

    return (
      <React.Suspense
        fallback={config && 'fallback' in config ? config.fallback : null}
      >
        {isSsrAndNeedsHydration && (
          <LazyMarkHydrationDone componentId={componentId} />
        )}

        <LazyLoaded {...(props as any)} ref={ref} />
      </React.Suspense>
    );
  });

  // @ts-expect-error -- adding a custom property to react component type
  Suspensified.preload = importer;

  return Suspensified as unknown as T & {preload: () => Promise<void>};
}

/**
 * Lazy load this too to be like the main component, which is lazy loaded as well.
 */
const LazyMarkHydrationDone = React.lazy(() =>
  import('./MarkHydrationDone').then(mod => ({
    default: mod.MarkHydrationDone,
  })),
);
